/**
 * VENDORED from tc-console/src/home/types.ts — DO NOT EDIT.
 *
 * Refresh with scripts/sync-home.sh; edit the canonical file instead. See the
 * script header for what is copied verbatim and what is an adaptation.
 */
/**
 * Home's document shape — the client mirror of phoenix `src/lib/home.ts`.
 *
 * Home is a website made of markdown: a set of captured pages, a hero, tiles that
 * promote what the OWNER wants promoted, and a little branding. The server does the
 * reading and the structure; this side only lays it out, so a content change is a
 * commit in a content repo rather than a release here.
 */

export interface HomeAction {
  label: string
  href: string
}

export interface HomeNavLink extends HomeAction {
  /** Present when the target is a page in this Home's own corpus. */
  file?: string
  /** A folder of pages, when the menu is derived from the doc tree (no explicit
   *  nav in home.json): folders are menus, pages are items. Parents have href
   *  '#' and open a dropdown rather than navigating. */
  children?: HomeNavLink[]
  /** The page's publish date (yyyy-mm-dd) — menus show it; folder items sort
   *  newest-first by it. */
  date?: string
  /** The page's last-updated date (yyyy-mm-dd), when the corpus tracks one.
   *  Menus prefer it over `date`: an Updated line beats a Published one. */
  updated?: string
  /** The page is private: it stays VISIBLE in menus, badged with a lock, but
   *  does not open for a reader who is not signed in. Absent = public. */
  private?: boolean
}

export interface HomeTile {
  title: string
  body: string
  action?: HomeAction
  image?: string
  meta?: string
  /** Open a Work Surface in the panel instead of following a link. The admin
   *  dashboard's tiles go somewhere in the product, not out to the web. */
  surface?: string
}

/** A number worth looking at. Dashboard only. */
export interface HomeStat {
  label: string
  value: string
  hint?: string
  surface?: string
  /** Daily counts across the window, zero-filled, oldest first. Absent when the
   *  client's indexes could not produce it in time — the number still stands. */
  series?: number[]
}

export interface HomeSection {
  id: string
  title: string
  kind: 'tiles' | 'callout' | 'prose'
  body?: string
  tiles: HomeTile[]
  actions: HomeAction[]
}

export interface HomeBranding {
  logoUrl?: string
  /** Home's own buttons and rules. The app theme — light/dark, font — stays the
   *  user's choice; an owner brands their content, not someone's eyes. */
  accent?: string
  heroImage?: string
  heroStyle?: 'image' | 'plain'
  columns?: 1 | 2 | 3
  showFooter?: boolean
}

export interface HomeDoc {
  title: string
  /** `content` = the website shape (guest, Learner View). `dashboard` = an admin's
   *  own numbers and surfaces. Very similar, deliberately not the same. */
  kind: 'content' | 'dashboard'
  stats: HomeStat[]
  /** What the numbers cover — shown under the row while they are client-wide
   *  rather than scoped to the viewer's own branches. */
  statsNote?: string
  /** The cadence every number answers for, and the cadences on offer. */
  windowDays?: number
  windows?: number[]
  branding: HomeBranding
  hero: { title: string; body: string; media?: string; actions: HomeAction[] }
  sections: HomeSection[]
  footer: HomeAction[]
  nav: HomeNavLink[]
  /** `host/path` (no scheme, no trailing slash) -> file, for every page held. */
  routes: Record<string, string>
  /** The page's raw markdown body — present only on article-shaped inner pages
   *  (nothing actionable). Rendered as one flowing document, hero+cards skipped. */
  article?: string
  /** The page's cover image — a banner at the very top of an article-shaped
   *  page. Set server-side (frontmatter `cover:`, or the page's lone image
   *  hoisted out of the body); already a resolved absolute URL. */
  cover?: string
  /** One-line generated summary of the page + its mp3 (a plain public https
   *  URL) — present when the door's voice summaries are on and this page was
   *  processed. The surface renders both as-is; generation lives server-side. */
  summary?: string
  audioUrl?: string
  /** Full-article narration mp3 (the whole page read aloud) — played from the
   *  title row's read-aloud button (TitleActions). */
  audioFullUrl?: string
  source: { file: string; lastmod?: string }
}

/** Match a link against the corpus the same way the server keyed it. */
export function fileForHref(doc: HomeDoc, href: string): string | undefined {
  const key = href.trim().replace(/^https?:\/\//i, '').replace(/\/+$/, '').toLowerCase()
  return doc.routes[key]
}

export const IMAGE_RE = /\.(png|jpe?g|gif|webp|svg)(\?|$)/i
